import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";

const COUNTRY_BY_SLUG = { australia: "AU", canada: "CA", uk: "UK" };

export function RichText({ text, className = "" }) {
  const blocks = (text || "").split(/\n\s*\n/);
  return (
    <div className={`space-y-4 ${className}`}>
      {blocks.map((b, i) => {
        const lines = b.split("\n").filter(Boolean);
        if (lines.length && lines.every((l) => /^\s*[-•]\s+/.test(l))) {
          return <ul key={i} className="list-disc pl-6 space-y-1.5">{lines.map((l, j) => <li key={j}>{l.replace(/^\s*[-•]\s+/, "")}</li>)}</ul>;
        }
        return <p key={i}>{lines.join(" ")}</p>;
      })}
    </div>
  );
}

export default function SeoPage() {
  const { country } = useParams();
  const [page, setPage] = useState(null); const [err, setErr] = useState(false);
  const slug = `english-for-migrants-${country}`;
  useEffect(() => {
    setPage(null); setErr(false);
    api.get(`/seo/pages/${slug}`).then(({ data }) => setPage(data.page)).catch(() => setErr(true));
  }, [slug]);

  const canonical = `https://englishready.online/english-for-migrants/${country}`;
  const jsonLd = useMemo(() => page ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
  } : null, [page]);
  useSeo({ title: page?.title, description: page?.meta_description, canonical, jsonLd });

  if (err) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="seo-page-missing">
      <h1 className="font-display font-extrabold text-3xl text-slate-900">This guide is being prepared</h1>
      <p className="text-slate-600 mt-3">Check back in a minute, or explore our country pages.</p>
      <Link to={`/country/${COUNTRY_BY_SLUG[country] || "AU"}`}><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-6">Country overview</Button></Link>
    </div>
  );
  if (!page) return <div className="max-w-3xl mx-auto px-4 py-24 text-slate-500" data-testid="seo-page-loading">Loading guide…</div>;

  return (
    <article data-testid="seo-page">
      <header className="rle-gradient-bg py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <span className="rle-eyebrow">Guide · {country.toUpperCase() === "UK" ? "United Kingdom" : country[0].toUpperCase() + country.slice(1)}</span>
          <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]" data-testid="seo-page-h1">{page.h1}</h1>
          <RichText text={page.intro} className="mt-6 text-base md:text-lg text-slate-700"/>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 h-12 px-6" data-testid="seo-hero-cta">Start practising free <ArrowRight size={16} className="ml-1"/></Button></Link>
            <Link to="/how-it-works"><Button variant="outline" className="rounded-full h-12 px-6">How it works</Button></Link>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-14">
        <nav className="rle-card p-5 text-sm" aria-label="Contents" data-testid="seo-toc">
          <div className="rle-eyebrow mb-2">In this guide</div>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 list-decimal pl-5 text-slate-700">
            {page.sections.map((s, i) => <li key={i}><a href={`#s-${i}`} className="hover:text-emerald-700">{s.heading}</a></li>)}
          </ol>
        </nav>

        {page.sections.map((s, i) => (
          <section key={i} id={`s-${i}`} data-testid={`seo-section-${i}`}>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">{s.heading}</h2>
            <RichText text={s.body} className="mt-4 text-slate-700 leading-relaxed"/>
          </section>
        ))}

        <section data-testid="seo-faqs">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">Frequently asked questions</h2>
          <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
            {page.faqs.map((f, i) => (
              <details key={i} className="group py-4" data-testid={`seo-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-slate-900">{f.question}<ChevronDown size={18} className="text-slate-400 transition-transform group-open:rotate-180"/></summary>
                <p className="mt-2 text-slate-700">{f.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="rounded-3xl bg-slate-900 text-white p-8 sm:p-12" data-testid="seo-cta">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl">{page.cta_heading}</h2>
          <p className="mt-3 text-slate-300">{page.cta_text}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register"><Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-6" data-testid="seo-bottom-cta">Create your free account</Button></Link>
            <Link to="/pricing"><Button variant="outline" className="rounded-full h-12 px-6 border-slate-600 text-white hover:bg-slate-800 hover:text-white">See plans</Button></Link>
          </div>
          <p className="mt-6 text-xs text-slate-400">English Ready is an educational language-practice platform. It does not provide immigration, legal, medical or financial advice.</p>
        </section>
      </div>
    </article>
  );
}
